import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Dropzone } from '@starjack/shared-components';
import { fetchAuthWrapper } from '@starjack/api';
import { useNotification } from '@starjack/features/notifications';
import type { Product } from '@starjack/entities/product';
import {
    TICKET_PDF_BACKGROUND_TYPES,
    PDF_BACKGROUND_TYPE_LABELS,
    PdfBackground,
    PdfBackgroundType,
    SHORT_PRODUCT_ID_REGEX,
} from '@/models/pdfBackground';
import { pdfBackgroundUrl } from '@/api/v2/pdfBackground';
import { B2BUserWithCredentials } from '@/models/b2b';

const ACCEPTED_PDF = { 'application/pdf': ['.pdf'] };

type Props = {
    open: boolean;
    onClose: () => void;
    b2bUsers: B2BUserWithCredentials[];
    products: Product[];
    existingBackgrounds: PdfBackground[];
    onUploaded: () => void;
};

type B2BOption = { id: string; label: string };
type ProductOption = { shortProductId: string; label: string };

export const PdfBackgroundUploadDialog: FunctionComponent<Props> = ({
    open,
    onClose,
    b2bUsers,
    products,
    existingBackgrounds,
    onUploaded,
}) => {
    const { addNotification } = useNotification();
    const [type, setType] = useState<PdfBackgroundType>(
        PdfBackgroundType.PICKUP_TICKET
    );
    const [userOption, setUserOption] = useState<B2BOption | null>(null);
    const [productOption, setProductOption] = useState<ProductOption | null>(
        null
    );
    const [file, setFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setType(PdfBackgroundType.PICKUP_TICKET);
        setUserOption(null);
        setProductOption(null);
        setFile(null);
    }, [open]);

    const userOptions = useMemo<B2BOption[]>(
        () =>
            [...b2bUsers]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((u) => ({ id: u.id, label: u.name })),
        [b2bUsers]
    );

    const productOptions = useMemo<ProductOption[]>(
        () =>
            products
                .filter((p) => !!p.shortProductId)
                .map((p) => ({
                    shortProductId: p.shortProductId,
                    label: p.name?.de
                        ? `${p.name.de} (${p.shortProductId})`
                        : p.shortProductId,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [products]
    );

    const duplicate = useMemo(() => {
        const targetUserId = userOption?.id ?? null;
        const targetShortProductId = productOption?.shortProductId ?? null;
        return existingBackgrounds.find(
            (bg) =>
                bg.type === type &&
                (bg.userId ?? null) === targetUserId &&
                (bg.shortProductId ?? null) === targetShortProductId
        );
    }, [existingBackgrounds, type, userOption, productOption]);

    const canSubmit = !!file && !submitting;

    const onDrop = (accepted: File[]) => {
        if (accepted.length) setFile(accepted[0]);
    };

    const handleUpload = async () => {
        if (!file) return;
        setSubmitting(true);
        try {
            const arrayBuffer = await file.arrayBuffer();
            const response = await fetchAuthWrapper(
                pdfBackgroundUrl(type, {
                    userId: userOption?.id,
                    shortProductId: productOption?.shortProductId,
                }),
                {
                    method: 'POST',
                    body: arrayBuffer,
                    headers: {
                        'Content-Type': 'application/pdf',
                        Accept: 'application/json',
                    },
                }
            );
            if (!response.ok) {
                throw new Error('Upload fehlgeschlagen.');
            }
            addNotification({
                message: duplicate
                    ? 'Vorlage ersetzt.'
                    : 'Vorlage hochgeladen.',
                type: 'success',
            });
            onUploaded();
            onClose();
        } catch {
            addNotification({
                message:
                    'Vorlage konnte nicht hochgeladen werden. Bitte erneut versuchen.',
                type: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>Neue Vorlage hochladen</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    <DialogContentText>
                        Wählen Sie den Tickettyp und optional einen B2B-User
                        und/oder ein Produkt. Beim Druck wird die spezifischste
                        Vorlage verwendet.
                    </DialogContentText>

                    <TextField
                        select
                        label='Tickettyp'
                        value={type}
                        onChange={(e) =>
                            setType(e.target.value as PdfBackgroundType)
                        }
                        fullWidth
                    >
                        {TICKET_PDF_BACKGROUND_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {PDF_BACKGROUND_TYPE_LABELS[t]}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Autocomplete
                        options={userOptions}
                        value={userOption}
                        onChange={(_, v) => setUserOption(v)}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='B2B-User (optional)'
                                placeholder='Keine Einschränkung'
                            />
                        )}
                    />
                    <Autocomplete
                        options={productOptions}
                        value={productOption}
                        onChange={(_, v) => setProductOption(v)}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) =>
                            o.shortProductId === v.shortProductId
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label='Produkt (optional)'
                                placeholder='Keine Einschränkung'
                            />
                        )}
                    />
                    {productOption &&
                        !SHORT_PRODUCT_ID_REGEX.test(
                            productOption.shortProductId
                        ) && (
                            <Alert severity='warning'>
                                Die Short-Product-ID „
                                {productOption.shortProductId}" enthält
                                unerlaubte Zeichen — Upload wird vom Backend
                                abgelehnt.
                            </Alert>
                        )}

                    {duplicate && (
                        <Alert severity='warning'>
                            Für diese Auswahl existiert bereits eine Vorlage.
                            Beim Hochladen wird sie überschrieben.
                        </Alert>
                    )}

                    <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>
                            PDF auswählen
                        </Typography>
                        <Dropzone
                            onDrop={onDrop}
                            accept={ACCEPTED_PDF}
                            multiple={false}
                        />
                        {file && (
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mt: 1 }}
                            >
                                Ausgewählt: {file.name} (
                                {(file.size / 1024).toFixed(1)} KB)
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Abbrechen
                </Button>
                <Button
                    variant='contained'
                    onClick={handleUpload}
                    disabled={!canSubmit}
                    startIcon={
                        submitting ? (
                            <CircularProgress size={16} color='inherit' />
                        ) : undefined
                    }
                >
                    {duplicate ? 'Ersetzen' : 'Hochladen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
